/* ============================================================
   Drawer menu v3 (menuv3) — panel navigation, lazy model
   loading and category chip filtering.
   The drawer itself (open/close/focus trap) stays on
   theme.Drawers in theme.js; this module only manages what
   happens inside #NavDrawer .menu-v3.
   ============================================================ */
(function() {
  'use strict';

  var CACHE_VERSION = 'v3';
  var CACHE_TTL_MS = 60 * 60 * 1000; // 1h
  var MAX_MODEL_PAGES = 4; // fragment paginates by 250 products; 4 pages = 1000

  var initializedRoot = null;

  function init() {
    var root = document.querySelector('#NavDrawer .menu-v3');
    if (!root || root === initializedRoot) return;
    initializedRoot = root;

    var drawerEl = document.getElementById('NavDrawer');
    var main = root.querySelector('[data-menu-main]');
    var panels = root.querySelectorAll('[data-menu-panel]');
    var modelsPanel = root.querySelector('[data-models-panel]');
    var openPanelEl = null;
    var openerEl = null;
    var fetchStarted = false;

    // Focusing an element inside a translateX'd panel makes the browser
    // scroll the overflow:hidden drawer horizontally to reveal it, which
    // leaves the whole menu shifted and clipped. Prevent + undo that.
    function safeFocus(el) {
      if (!el) return;
      try {
        el.focus({ preventScroll: true });
      } catch (err) {
        el.focus();
      }
      resetScroll();
    }

    function resetScroll() {
      root.scrollLeft = 0;
      if (drawerEl) drawerEl.scrollLeft = 0;
      window.requestAnimationFrame(function() {
        root.scrollLeft = 0;
        if (drawerEl) drawerEl.scrollLeft = 0;
      });
    }

    /* ---------- panel navigation ---------- */

    function setMainInert(inert) {
      if (!('inert' in main)) return;
      main.inert = inert;
    }

    function openPanel(panel, opener) {
      if (openPanelEl === panel) return;
      if (openPanelEl) closePanel(false);
      openPanelEl = panel;
      openerEl = opener || null;
      panel.classList.add('is-open');
      panel.setAttribute('aria-hidden', 'false');
      root.classList.add('menu-v3--pushed');
      setMainInert(true);
      safeFocus(panel.querySelector('[data-panel-back]'));
    }

    function closePanel(restoreFocus) {
      var panel = openPanelEl;
      if (!panel) return;
      openPanelEl = null;
      panel.classList.add('is-leaving');
      panel.classList.remove('is-open');
      panel.setAttribute('aria-hidden', 'true');
      root.classList.remove('menu-v3--pushed');
      setMainInert(false);
      var done = function() {
        panel.classList.remove('is-leaving');
        panel.removeEventListener('transitionend', done);
      };
      panel.addEventListener('transitionend', done);
      window.setTimeout(done, 500);
      if (restoreFocus !== false && openerEl) {
        safeFocus(openerEl);
      }
      openerEl = null;
    }

    function resetPanels() {
      // Instant reset (used when the drawer opens): no animation.
      if (!openPanelEl) return;
      var panel = openPanelEl;
      openPanelEl = null;
      openerEl = null;
      panel.classList.remove('is-open', 'is-leaving');
      panel.setAttribute('aria-hidden', 'true');
      root.classList.remove('menu-v3--pushed');
      setMainInert(false);
      resetScroll();
    }

    root.addEventListener('click', function(evt) {
      var openBtn = evt.target.closest('[data-panel-open]');
      if (openBtn) {
        var target = document.getElementById(openBtn.getAttribute('data-panel-open'));
        if (target) openPanel(target, openBtn);
        return;
      }
      var backBtn = evt.target.closest('[data-panel-back]');
      if (backBtn) {
        closePanel(true);
      }
    });

    document.addEventListener('drawerOpen.NavDrawer', function() {
      resetPanels();
      startFetch();
    });

    /* ---------- lazy model fragment ---------- */

    function cacheKey(url) {
      var customerType = document.body.getAttribute('data-customer-type') || 'b2c';
      return 'menuv3:models:' + CACHE_VERSION + ':' + customerType + ':' + url;
    }

    function readCache(key) {
      try {
        var raw = window.sessionStorage.getItem(key);
        if (!raw) return null;
        var entry = JSON.parse(raw);
        if (!entry || !entry.html || (Date.now() - entry.t) > CACHE_TTL_MS) return null;
        return entry.html;
      } catch (err) {
        return null;
      }
    }

    function writeCache(key, html) {
      try {
        window.sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), html: html }));
      } catch (err) {
        /* storage full/unavailable — non-fatal */
      }
    }

    function startFetch() {
      if (fetchStarted || !modelsPanel) return;
      fetchStarted = true;
      var url = modelsPanel.getAttribute('data-fetch-url');
      if (!url) return;

      var key = cacheKey(url);
      var cached = readCache(key);
      if (cached) {
        hydrate(cached);
        return;
      }

      fetchAllPages(url)
        .then(function(html) {
          writeCache(key, html);
          hydrate(html);
        })
        .catch(function() {
          showFallback();
        });
    }

    function fetchPage(pageUrl) {
      return fetch(pageUrl, { credentials: 'same-origin' }).then(function(res) {
        if (!res.ok) throw new Error('menu-models fetch failed: ' + res.status);
        return res.text();
      });
    }

    function parsePayload(html) {
      try {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.querySelector('.menu-v3__models-payload');
      } catch (err) {
        return null;
      }
    }

    // The fragment paginates by 250 products, so a big models collection is
    // spread over several pages. Fetch the remaining pages and merge their
    // A–Å models (deduped by model key) and chips into page 1's payload,
    // then cache/hydrate the merged HTML. Pills/top-4 stay page 1 only —
    // that's the best-selling head of the list.
    function fetchAllPages(url) {
      return fetchPage(url).then(function(html) {
        var payload = parsePayload(html);
        if (!payload) return html; // hydrate() falls back on missing payload
        var pages = parseInt(payload.getAttribute('data-pages'), 10) || 1;
        if (pages <= 1) return html;

        var extras = [];
        var lastPage = Math.min(pages, MAX_MODEL_PAGES);
        for (var p = 2; p <= lastPage; p++) {
          extras.push(
            fetchPage(url + '&page=' + p).then(parsePayload).catch(function() { return null; })
          );
        }
        return Promise.all(extras).then(function(payloads) {
          mergePayloads(payload, payloads.filter(Boolean));
          if (pages <= MAX_MODEL_PAGES) payload.removeAttribute('data-truncated');
          return payload.outerHTML;
        });
      });
    }

    function mergePayloads(base, rest) {
      var azTpl = base.querySelector('template[data-part="az"]');
      if (!azTpl || !rest.length) return;
      var chipsTpl = base.querySelector('template[data-part="chips"]');

      var models = [];
      var seen = {};
      function collect(tpl) {
        if (!tpl) return;
        tpl.content.querySelectorAll('.menu-v3__model').forEach(function(el) {
          var nameEl = el.querySelector('.menu-v3__model-name');
          var k = el.getAttribute('data-key') || (nameEl ? nameEl.textContent : '');
          if (!k || seen[k]) return;
          seen[k] = true;
          models.push(el.cloneNode(true));
        });
      }

      collect(azTpl);
      rest.forEach(function(p) {
        collect(p.querySelector('template[data-part="az"]'));
        if (!chipsTpl) return;
        var have = {};
        chipsTpl.content.querySelectorAll('[data-cat-filter]').forEach(function(c) {
          have[c.getAttribute('data-cat-filter')] = true;
        });
        var pChips = p.querySelector('template[data-part="chips"]');
        if (!pChips) return;
        pChips.content.querySelectorAll('[data-cat-filter]').forEach(function(c) {
          var f = c.getAttribute('data-cat-filter');
          if (!have[f]) {
            have[f] = true;
            chipsTpl.content.appendChild(c.cloneNode(true));
          }
        });
      });

      function nameOf(el) {
        var nameEl = el.querySelector('.menu-v3__model-name');
        return nameEl ? nameEl.textContent : '';
      }
      models.sort(function(a, b) {
        return nameOf(a).localeCompare(nameOf(b), 'nb', { sensitivity: 'base' });
      });

      var frag = document.createDocumentFragment();
      var currentLetter = null;
      var group = null;
      models.forEach(function(el) {
        var letter = nameOf(el).charAt(0).toUpperCase();
        if (letter !== currentLetter) {
          currentLetter = letter;
          group = document.createElement('div');
          group.className = 'menu-v3__letter-group';
          group.setAttribute('data-letter', letter);
          var letterEl = document.createElement('div');
          letterEl.className = 'menu-v3__letter';
          letterEl.textContent = letter;
          group.appendChild(letterEl);
          frag.appendChild(group);
        }
        group.appendChild(el);
      });
      while (azTpl.content.firstChild) {
        azTpl.content.removeChild(azTpl.content.firstChild);
      }
      azTpl.content.appendChild(frag);

      base.setAttribute('data-model-count', String(models.length));
      var subtitle = base.getAttribute('data-subtitle');
      if (subtitle) {
        base.setAttribute('data-subtitle', subtitle.replace(/\d+/, String(models.length)));
      }
    }

    function hydrate(html) {
      var doc;
      try {
        doc = new DOMParser().parseFromString(html, 'text/html');
      } catch (err) {
        showFallback();
        return;
      }
      var payload = doc.querySelector('.menu-v3__models-payload');
      if (!payload) {
        showFallback();
        return;
      }

      fillSlot(root, 'pills', payload);
      fillSlot(modelsPanel, 'chips', payload);
      fillSlot(modelsPanel, 'top4', payload);
      fillSlot(modelsPanel, 'az', payload);

      var subtitle = modelsPanel.querySelector('[data-panel-subtitle]');
      if (subtitle && payload.getAttribute('data-subtitle')) {
        subtitle.textContent = payload.getAttribute('data-subtitle');
      }

      var modellerRowCount = payload.getAttribute('data-model-count');
      if (!modellerRowCount || modellerRowCount === '0') {
        showFallback();
      }
    }

    function fillSlot(scope, part, payload) {
      var slot = scope.querySelector('[data-slot="' + part + '"]');
      var tpl = payload.querySelector('template[data-part="' + part + '"]');
      if (!slot || !tpl) return;
      // Slots marked data-static were server-rendered from the model-ranked
      // bestsellers shop metafield (custom.hoka_modeller_bestselgere) — keep
      // them, the fragment is only a fallback.
      if (slot.hasAttribute('data-static')) return;
      slot.innerHTML = '';
      slot.appendChild(tpl.content.cloneNode(true));
    }

    function showFallback() {
      if (!modelsPanel) return;
      modelsPanel.querySelectorAll('.menu-v3__pill--skeleton, .menu-v3__chip--skeleton, .menu-v3__best-card--skeleton').forEach(function(el) {
        el.remove();
      });
      root.querySelectorAll('[data-slot="pills"] .menu-v3__pill--skeleton').forEach(function(el) {
        el.remove();
      });
      var fallback = modelsPanel.querySelector('.menu-v3__models-fallback');
      if (fallback) fallback.hidden = false;
    }

    /* ---------- category chip filtering ---------- */

    if (modelsPanel) {
      modelsPanel.addEventListener('click', function(evt) {
        var chip = evt.target.closest('[data-cat-filter]');
        if (!chip) return;
        modelsPanel.querySelectorAll('[data-cat-filter]').forEach(function(c) {
          c.classList.toggle('is-active', c === chip);
        });
        applyFilter(chip.getAttribute('data-cat-filter'));
      });
    }

    function applyFilter(cat) {
      var showAll = cat === 'alle';
      modelsPanel.querySelectorAll('[data-cat]').forEach(function(el) {
        var cats = (el.getAttribute('data-cat') || '').split(/\s+/);
        var visible = showAll || cats.indexOf(cat) !== -1;
        el.classList.toggle('is-filtered-out', !visible);
      });
      modelsPanel.querySelectorAll('.menu-v3__letter-group').forEach(function(group) {
        var anyVisible = group.querySelector('.menu-v3__model:not(.is-filtered-out)');
        group.classList.toggle('is-filtered-out', !anyVisible);
      });
    }

    /* ---------- desktop drag-to-scroll (pills / chips) ---------- */
    // Touch already scrolls overflow-x natively. Mouse/pen does not,
    // so click-drag the strip the same way. A real drag suppresses the
    // click so a pill/chip is not followed after a swipe.

    function bindDragScroll(el) {
      if (!el || el.getAttribute('data-drag-bound') === '1') return;
      el.setAttribute('data-drag-bound', '1');

      var startX = 0;
      var startLeft = 0;
      var pointerId = null;
      var dragged = false;
      var THRESHOLD = 5;

      function onMove(e) {
        if (pointerId !== e.pointerId) return;
        var dx = e.clientX - startX;
        if (!dragged && Math.abs(dx) > THRESHOLD) {
          dragged = true;
          el.classList.add('is-dragging');
          // Capture only after a real drag so a click is not retargeted
          // onto the strip (which made chip/pill buttons miss their click).
          try { el.setPointerCapture(e.pointerId); } catch (err) {}
        }
        if (dragged) {
          el.scrollLeft = startLeft - dx;
        }
      }

      function endDrag(e) {
        if (pointerId !== e.pointerId) return;
        pointerId = null;
        el.classList.remove('is-dragging');
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', endDrag);
        document.removeEventListener('pointercancel', endDrag);
        if (dragged) {
          window.setTimeout(function() { dragged = false; }, 0);
        }
      }

      el.addEventListener('pointerdown', function(e) {
        if (e.pointerType === 'touch') return;
        if (e.button !== 0) return;
        pointerId = e.pointerId;
        dragged = false;
        startX = e.clientX;
        startLeft = el.scrollLeft;
        document.addEventListener('pointermove', onMove);
        document.addEventListener('pointerup', endDrag);
        document.addEventListener('pointercancel', endDrag);
      });

      el.addEventListener('click', function(e) {
        if (!dragged) return;
        e.preventDefault();
        e.stopPropagation();
        dragged = false;
      }, true);

      el.addEventListener('dragstart', function(e) {
        e.preventDefault();
      });
    }

    root.querySelectorAll('.menu-v3__pills, .menu-v3__chips').forEach(bindDragScroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Impulse re-renders sections in the theme editor and fires page:loaded.
  document.addEventListener('page:loaded', init);
  // Theme editor / theme-dev hot reload replace the header section DOM.
  document.addEventListener('shopify:section:load', init);
})();
